import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { NavLink, useNavigate } from 'react-router-dom';

import DefaultAuth from 'layouts/auth/Default';
import { resetPasswordApi } from 'features/auth/auth.api';

import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';

import illustration from 'assets/img/auth/auth.png';

function ResetPassword() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorDetails = useColorModeValue('navy.700', 'secondaryGray.600');
  const textColorBrand = useColorModeValue('brand.500', 'white');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { resetPasswordStatus, resetPasswordMessage, resetPasswordToken } = useSelector(
    (state) => state.auth,
  );

  // Local state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Toggle password visibility
  const handleShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Check form validity
  const validateForm = () => {
    const newErrors = {};
    if (!newPassword) {
      newErrors.newPassword = 'New password is required.';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required.';
    }
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit new password
  const handleResetPassword = () => {
    if (validateForm()) {
      dispatch(resetPasswordApi({ token: resetPasswordToken, newPassword }));
    }
  };

  // If reset is successful, navigate to login page
  useEffect(() => {
    if (resetPasswordStatus === 'successful') {
      navigate('/auth/sign-in');
    }
  }, [resetPasswordStatus, navigate]);

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="50%"
        align="center"
        justify="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Reset Password
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Enter your new password and confirm it to reset.
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          {/* New Password */}
          <FormControl mb="20px" isInvalid={!!errors.newPassword}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              New Password<Text color={textColorBrand}>*</Text>
            </FormLabel>
            <InputGroup size="md">
              <Input
                variant="auth"
                fontSize="sm"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                mb="8px"
                fontWeight="500"
                size="lg"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: 'pointer' }}
                  as={showNewPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleShowNewPassword}
                />
              </InputRightElement>
            </InputGroup>
            {errors.newPassword && (
              <Text color="red.500" fontSize="sm">
                {errors.newPassword}
              </Text>
            )}
          </FormControl>

          {/* Confirm Password */}
          <FormControl mb="20px" isInvalid={!!errors.confirmPassword}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Confirm Password<Text color={textColorBrand}>*</Text>
            </FormLabel>
            <InputGroup size="md">
              <Input
                variant="auth"
                fontSize="sm"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your new password"
                mb="8px"
                fontWeight="500"
                size="lg"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: 'pointer' }}
                  as={showConfirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleShowConfirmPassword}
                />
              </InputRightElement>
            </InputGroup>
            {errors.confirmPassword && (
              <Text color="red.500" fontSize="sm">
                {errors.confirmPassword}
              </Text>
            )}
          </FormControl>

          {/* Submit Button */}
          <Button
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%"
            h="50"
            mb="18px"
            isLoading={resetPasswordStatus === 'pending'}
            loadingText="Resetting Password..."
            onClick={handleResetPassword}
          >
            Reset Password
          </Button>

          {/* Display error or info message */}
          {resetPasswordMessage && (
            <Text color="red.500" fontSize="sm" mb="8px">
              {resetPasswordMessage}
            </Text>
          )}

          {/* Link back to sign-in */}
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="start"
            maxW="100%"
            mt="0px"
          >
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              Back to
              <NavLink to="/auth/sign-in">
                <Text color={textColorBrand} as="span" ms="5px" fontWeight="500">
                  Sign In
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default ResetPassword;
